import { FormsProvider } from '@superdispatch/forms';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { ReactNode, Suspense } from 'react';
import { TEN_MINUTES, TEN_SECONDS } from 'shared/constants/NumberConstants';
import { AppErrorBoundary } from 'shared/errors/AppErrorBoundary';
import { defaultFormErrorsGetter } from 'shared/helpers/FormHelpers';
import { AppLayoutManagerProvider } from 'shared/layout/AppLayoutManager';
import { SplashScreen } from 'shared/layout/SplashScreen';
import { AppThemeProvider } from 'shared/theme/AppThemeProvider';
import { redirectToBasePath } from 'shared/utils/RouterUtils';
import { useSentryFeatureToggles } from 'shared/helpers/useSentryFeatureToggles';
import { BotpressProvider } from 'shared/modules/botpress/BotpressProvider';

function SentryFeatureTogglesProvider({
  children,
}: {
  children: ReactNode;
}): JSX.Element {
  useSentryFeatureToggles();
  return children as JSX.Element;
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: false,
      retryOnMount: false, // Disable retry on mount to avoid infinite loading state
      staleTime: TEN_SECONDS,
      gcTime: TEN_MINUTES,
    },
  },
});

interface RootContextProviderProps {
  children?: ReactNode;
  disableBotpress?: boolean;
}

redirectToBasePath();

export function AppContainer({
  children,
  disableBotpress = false,
}: RootContextProviderProps) {
  return (
    <Suspense fallback={<SplashScreen />}>
      <QueryClientProvider client={queryClient}>
        <AppErrorBoundary>
          <SentryFeatureTogglesProvider>
            <AppThemeProvider>
              <FormsProvider getFormErrors={defaultFormErrorsGetter}>
                <AppLayoutManagerProvider>
                  {!('Cypress' in window) &&
                    import.meta.env.MODE === 'development' && (
                      <ReactQueryDevtools
                        initialIsOpen={false}
                        buttonPosition="bottom-left"
                      />
                    )}

                  {disableBotpress ? (
                    children
                  ) : (
                    <BotpressProvider>{children}</BotpressProvider>
                  )}
                </AppLayoutManagerProvider>
              </FormsProvider>
            </AppThemeProvider>
          </SentryFeatureTogglesProvider>
        </AppErrorBoundary>
      </QueryClientProvider>
    </Suspense>
  );
}
